/**
 * The lines of a reviewed online-platform file that are not matched to a menu
 * dish, as the Not in Menu lines /online-sales/submit records against the order
 * (spec 2026-10-26-tsr-add-as-not-in-menu). Remembered skips are included: the
 * server sets them aside itself and keeps their money, as the in-store TSR does.
 */
const round2 = (n) => Math.round(n * 100) / 100;

export function onlineUnmappedItems(reviewItems) {
  return (reviewItems || [])
    .filter((i) => i && !(i.is_matched && i.mapped_menu_item_id))
    .map((i) => {
      const quantity = Number(i.total_quantity) || 0;
      const unitPrice = Number(i.true_selling_price || i.unit_price) || 0;
      const stated = Number(i.total_amount);
      return {
        itemName: String(i.item_name || '').trim(),
        quantity,
        unitPrice,
        amount: round2(Number.isFinite(stated) && stated > 0 ? stated : quantity * unitPrice),
        reason: 'not_in_menu',
      };
    })
    .filter((l) => l.itemName);
}

/**
 * An order report's lines not on the menu, as each date's Not in Menu lines: one per
 * name and date, plates and money summed. Cancelled orders and dates already imported
 * are left out, exactly as their matched lines are.
 * @returns {Map<string, Array<{itemName, quantity, unitPrice, amount, reason}>>}
 */
export function orderReportUnmappedByDate(orders, { importedDates = new Set(), priceOf = (it) => Number(it.true_selling_price) || 0 } = {}) {
  const byDate = new Map();
  (orders || []).forEach((o, idx) => {
    if (!o || o.is_cancelled || !o.order_date || importedDates.has(o.order_date)) return;
    (o.items || []).forEach((it, ii) => {
      if (it.is_matched) return;
      const name = String(it.item_name || '').trim();
      if (!name) return;
      const quantity = Number(it.quantity) || 0;
      const lines = byDate.get(o.order_date) || new Map();
      const e = lines.get(name) || { itemName: name, quantity: 0, amount: 0 };
      e.quantity += quantity;
      e.amount += priceOf(it, idx, ii) * quantity;
      lines.set(name, e);
      byDate.set(o.order_date, lines);
    });
  });
  const out = new Map();
  for (const [date, lines] of byDate) {
    out.set(date, [...lines.values()].map((e) => ({
      itemName: e.itemName,
      quantity: e.quantity,
      unitPrice: e.quantity > 0 ? round2(e.amount / e.quantity) : 0,
      amount: round2(e.amount),
      reason: 'not_in_menu',
    })));
  }
  return out;
}
